import { useApi } from '@hooks/useApi';
import { Autocomplete, Box, Popper, Stack, styled, TextField, Typography } from '@mui/material';
import { getDecimalPlaces, getLotValue } from '@pages/ocl-trading/utils';
import { LadderSettings } from '@protos/ladders';
import { Product } from '@protos/product';
import { useUserContext } from '@shared/contexts/UserContextProvider';
import { useUserProductsAndTenorsContext } from '@shared/contexts/UserProductsAndTenorsProvider';
import { useCallback, useEffect, useMemo, useState } from 'react';
import NumberInput from '../NumberInput';

const SmallAutocomplete = styled(Autocomplete)({
  width: '125px',
  borderRadius: 0,
  '& .MuiAutocomplete-inputRoot': {
    padding: 0,
    fontSize: 11,
    height: 25,
    borderRadius: 0,
  },
  '& .MuiSvgIcon-root': {
    fontSize: 20,
    padding: 0,
  },
});

const StyledPopper = styled(Popper)({
  '& .MuiAutocomplete-listbox': {
    fontSize: 11,
  },
});

interface SpreadSettingsProps {
  spreadSettings: LadderSettings;
  setSpreadSettings: (settings: LadderSettings) => void;
  stopEditing: () => void;
}

export const SpreadSettings = ({ spreadSettings, setSpreadSettings, stopEditing }: SpreadSettingsProps) => {
  const { apiClient } = useApi();
  const user = useUserContext();
  const { userProducts } = useUserProductsAndTenorsContext();

  const allowedOCLTradingProductsForUser = useMemo(() => user?.ladder_limits.map(limit => limit.symbol) || [], [user?.ladder_limits]);

  const allTradableUserProducts = useMemo(
    () => userProducts.filter(product => allowedOCLTradingProductsForUser.includes(product.symbol) && product.product_type === 'calendar'),
    [userProducts, allowedOCLTradingProductsForUser]
  );
  const allSpreadProducts = useMemo(
    () => userProducts.filter(product => allTradableUserProducts.find(p => p.symbol === product.symbol)),
    [userProducts, allTradableUserProducts]
  );

  const [selectedValue, setSelectedValue] = useState<Product>();
  const [lotValue, setLotValue] = useState('');

  const onProductChange = useCallback(
    (newProductValue: string) => {
      const product = allSpreadProducts.find(p => p.symbol === newProductValue);
      if (product) {
        stopEditing();
        setSelectedValue(product);
        setSpreadSettings({ ...spreadSettings, symbol: product.symbol, tick_size: getDecimalPlaces(product.tick_size) });
      }
    },
    [allSpreadProducts, setSpreadSettings, spreadSettings]
  );

  const onValidityChange = useCallback(
    (value: number | undefined) => {
      if (!value) return;

      setSpreadSettings({ ...spreadSettings, validity: value });
    },
    [setSpreadSettings, spreadSettings]
  );

  const onSizeChange = useCallback(
    (value: number | undefined) => {
      if (!value) return;
      setSpreadSettings({ ...spreadSettings, size: value });
    },
    [setSpreadSettings, spreadSettings]
  );

  useEffect(() => {
    if (!allSpreadProducts.length) return;

    if (!selectedValue) {
      const value = allSpreadProducts.find(p => p.symbol === spreadSettings.symbol) || allSpreadProducts[0];
      setSelectedValue(value);
      setSpreadSettings({ ...spreadSettings, tick_size: getDecimalPlaces(value.tick_size) });
    } else if (selectedValue.symbol !== spreadSettings.symbol) {
      setSpreadSettings({ ...spreadSettings, symbol: selectedValue.symbol, tick_size: getDecimalPlaces(selectedValue.tick_size) });
    }
  }, [spreadSettings, selectedValue, allSpreadProducts]);

  useEffect(() => {
    if (!apiClient || !selectedValue) {
      setLotValue('');
      return;
    }
    getLotValueFromSelectedProduct();
  }, [apiClient, selectedValue]);

  const getLotValueFromSelectedProduct = async () => {
    const lot = await getLotValue(selectedValue!, apiClient!);
    setLotValue(lot);
  };

  if (!selectedValue) return null;

  return (
    <Stack display="flex" flex={1} gap={2}>
      <Stack>
        <Typography variant="h6" style={{ marginRight: 'auto' }}>
          {selectedValue.full_name}
        </Typography>
        <Typography fontSize={10}>{lotValue}</Typography>
      </Stack>
      <Box display="flex" flex={1} justifyContent="start" flexWrap="wrap">
        <NumberInput
          value={spreadSettings.validity.toString()}
          onChange={onValidityChange}
          label="Validity (s)"
          min={1}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '11px',
              '--Input-minHeight': '0',
            },
            '& .MuiInputBase-root': {
              borderRadius: '0px',
              height: 25,
            },
          }}
        />
        <NumberInput
          value={spreadSettings.size.toString()}
          onChange={onSizeChange}
          label="Lots"
          min={1}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '11px',
              '--Input-minHeight': '0',
            },
            '& .MuiInputBase-root': {
              borderRadius: '0px',
              height: 25,
            },
          }}
        />

        <SmallAutocomplete
          size="small"
          disableClearable
          options={allSpreadProducts}
          getOptionLabel={(option: unknown) => (option as Product).symbol.toUpperCase()}
          value={selectedValue}
          onChange={(_, newValue) => newValue && onProductChange((newValue as Product).symbol)}
          autoHighlight
          PopperComponent={props => <StyledPopper {...props} />}
          renderInput={params => <TextField {...params} variant="outlined" tabIndex={-1} />}
          isOptionEqualToValue={(option, value) => (option as Product).symbol === (value as Product).symbol}
          sx={{
            '& .MuiAutocomplete-input': { fontSize: 11, paddingLeft: 1.5 },
          }}
        />
      </Box>
    </Stack>
  );
};
